import { Link } from "@mui/material";
import Box from "@mui/material/Box";
import {
  getGridNumericOperators,
  GridCellParams,
  GridColDef,
  GridFilterOperator,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import chroma from "chroma-js";
import React from "react";

const scale = chroma.scale([
  "rgba(0,0,255,0.5)",
  "#C2C2C2",
  "rgba(255,0,0,0.5)",
]);

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#073060ff", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#61b8ffff", "#FFFFFF"],
  DET: ["#0C2C56", "#f62e23ff", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#143680ff", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#be8e14ff", "#FFFFFF"],
  STL: ["#BE0A15", "#b6b31bff", "#FFFFFF"],
  LAD: ["#005A9D", "#005184ff", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#000000ff", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

// The heat-map columns (`*_p`) hold the percentile in the row data but display
// the underlying raw value, so a CSV/Excel export of those fields emits the
// percentile. Tag their exported header (and column/filter panels) as a
// percentile while keeping the short on-screen header unchanged, so the export
// clearly distinguishes them from the matching raw value columns.
const labelPercentileColumns = (cols: GridColDef<any>[]): GridColDef<any>[] =>
  cols.map((col) => {
    if (!col.field.endsWith("_p") || col.field === "score_p") return col;
    const display = col.headerName ?? col.field;
    return {
      ...col,
      headerName: display + " %ile",
      renderHeader: () => display,
    };
  });

// ─── Numeric filtering ──────────────────────────────────────────────────────
// Columns default to the string operators (contains / equals / starts with).
// Every column that isn't an identifier holds a number, so swap in the numeric
// set to get >, >=, <, <=, =, != in the filter panel. The comparison always runs
// against the number the cell displays, which is NOT `row[field]` for the
// heat-map columns (keyed to the `_p` percentile) or for the rate columns whose
// valueFormatter scales a fraction to a percentage.

const TEXT_FILTER_FIELDS = new Set([
  "name",
  "player_name",
  "batter_name",
  "pitcher_name",
  "afl_team",
  "MLB_AbbName",
  "Position",
  "role",
  "pitch_name",
]);

// `<stat>_p` cells whose displayed value doesn't live under `<stat>`.
const RAW_FILTER_FIELDS: Record<string, string> = { velo_p: "max_velo" };

// Composite scores have no raw counterpart — the cell prints the percentile as a %.
const SCORE_FILTER_FIELDS = new Set(["score_p", "p_agg", "power_agg", "d_agg"]);

const parseDisplayed = (text: any): number | null => {
  if (typeof text === "number") return Number.isNaN(text) ? null : text;
  const cleaned = String(text ?? "").replace(/[^0-9.eE+-]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
};

// `>=` first so it is the default in both the filter panel and the header
// filter row — on a stat leaderboard "at least" beats "exactly equal to".
const STAT_OPERATOR_ORDER = [">=", "<=", ">", "<", "=", "!=", "isEmpty", "isNotEmpty", "isAnyOf"];

const STAT_OPERATOR_SYMBOLS: Record<string, string> = {
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
  "=": "=",
  "!=": "≠",
};

const statOperatorOrder = (
  ops: GridFilterOperator<any, any, any>[]
): GridFilterOperator<any, any, any>[] =>
  [...ops]
    .sort((a, b) => STAT_OPERATOR_ORDER.indexOf(a.value) - STAT_OPERATOR_ORDER.indexOf(b.value))
    .map((op) => {
      const symbol = STAT_OPERATOR_SYMBOLS[op.value];
      return symbol ? { ...op, label: symbol, headerLabel: symbol } : op;
    });

export const numericOperatorsOn = (
  read: (row: any, column: any, apiRef: any) => any
): GridFilterOperator<any, any, any>[] =>
  statOperatorOrder(
    getGridNumericOperators().map((op) => ({
      ...op,
      getApplyFilterFn: (filterItem, column) => {
        const apply = op.getApplyFilterFn(filterItem, column as any);
        if (!apply) return null;
        return (_value: any, row: any, col: any, apiRef: any) =>
          apply(read(row, col, apiRef), row, col, apiRef);
      },
    }))
  );

const numericFilterFor = (col: GridColDef<any>): GridFilterOperator<any, any, any>[] => {
  const { field } = col;
  if (SCORE_FILTER_FIELDS.has(field)) {
    return numericOperatorsOn((row) =>
      row[field] == null ? null : Number(row[field]) * 100
    );
  }
  const raw =
    RAW_FILTER_FIELDS[field] ?? (field.endsWith("_p") ? field.slice(0, -2) : null);
  if (raw) return numericOperatorsOn((row) => row[raw]);
  if (col.valueFormatter) {
    return numericOperatorsOn((row, column, apiRef) => {
      try {
        return parseDisplayed(
          (column.valueFormatter as any)(row[field], row, column, apiRef)
        );
      } catch {
        return null; // several formatters assume a non-null value
      }
    });
  }
  return statOperatorOrder(
    getGridNumericOperators() as GridFilterOperator<any, any, any>[]
  );
};

// The API fills missing stats with the STRING "-", and the default comparator
// falls back to string ordering as soon as either side is a string, which
// scatters those rows through the sorted column. Nulls land first ascending /
// last descending, matching MUI's own gridNumberComparator.
const numericSortComparator = (a: any, b: any) => {
  const x = parseDisplayed(a);
  const y = parseDisplayed(b);
  if (x === null) return y === null ? 0 : -1;
  if (y === null) return 1;
  return x - y;
};

// Most formatters/renderers assume a number. "-" (what the API's fillna sends
// for a missing stat) either throws — taking the whole grid down — or survives
// the arithmetic as the literal text "NaN". Sorting ascending pulls every
// missing-value row into the viewport at once, so guard centrally rather than
// at ~200 call sites.
const MISSING_CELL = <Box sx={{ width: "100%", textAlign: "center" }}>-</Box>;

const isMissingValue = (value: any) =>
  value === "-" ||
  value === null ||
  value === undefined ||
  (typeof value === "number" && !Number.isFinite(value));

const safeFormatter =
  (format: any) =>
  (...args: any[]) => {
    if (isMissingValue(args[0])) return "-";
    try {
      const text = format(...args);
      return typeof text === "string" && /NaN|Infinity/.test(text) ? "-" : text;
    } catch {
      return "-";
    }
  };

const safeRenderCell =
  (render: any) =>
  (params: GridRenderCellParams) => {
    if (isMissingValue(params.value)) return MISSING_CELL;
    try {
      return render(params);
    } catch {
      return MISSING_CELL;
    }
  };

export const withNumericFilters = (cols: GridColDef<any>[]): GridColDef<any>[] =>
  cols.map((col) => {
    const guarded: GridColDef<any> = { ...col };
    if (col.valueFormatter) guarded.valueFormatter = safeFormatter(col.valueFormatter);
    if (col.renderCell) guarded.renderCell = safeRenderCell(col.renderCell);
    if (TEXT_FILTER_FIELDS.has(col.field) || col.filterOperators) return guarded;
    return {
      ...guarded,
      filterOperators: numericFilterFor(col),
      sortComparator: col.sortComparator ?? numericSortComparator,
    };
  });

const hitter_columns_defs: GridColDef<any>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Link href={"/player/" + params.row.id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const team = teamColors[params.value] ? params.value : "FA";
      const color = teamColors[team][0]

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[team][2],
            textShadow: "-1px -1px 0 " + teamColors[team][1] + ", 1px -1px 0 " + teamColors[team][1] + ", -1px 1px 0 " + teamColors[team][1] + ", 1px 1px 0 " + teamColors[team][1] + "",
          }}
        >
          {(params.value)}
        </Box>
      );
    },
  },
  { field: "Position", headerName: "Pos", width: 80 },
  { field: "age", headerName: "Age", width: 60 },
  { field: "pa", headerName: "PA", width: 60 },
  { field: "ab", headerName: "AB", width: 60 },
  { field: "pitches", headerName: "Pitches", width: 70 },
  {
    field: "xwoba_p",
    headerName: "xwOBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xwoba.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "woba_p",
    headerName: "wOBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.woba.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "wobacon_p",
    headerName: "wOBACON",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.wobacon != null
            ? Number(params.row.wobacon).toFixed(3).toString().replace(/^0/, "")
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "score_p",
    headerName: "PS Score",
    width: 120,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "p_agg",
    headerName: "Agg",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "power_agg",
    headerName: "Power",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "d_agg",
    headerName: "Discipline",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "xba_p",
    headerName: "xBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xba.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xslg_p",
    headerName: "xSLG",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xslg.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "barrelbbe_p",
    headerName: "Barrel%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.barrelbbe.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "bbrate_p",
    headerName: "BB%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.bbrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "chaserate_p",
    headerName: "Chase%",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.chaserate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev_p",
    headerName: "EV",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.ev.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "maxev_p",
    headerName: "Max EV",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.maxev.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev50_p",
    headerName: "50th% EV",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.ev50.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev90_p",
    headerName: "90th% EV",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.ev90.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "hhrate_p",
    headerName: "Hard Hit%",
    width: 110,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.hhrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "krate_p",
    headerName: "K%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.krate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "langle_p",
    headerName: "L°",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.langle.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "whiffrate_p",
    headerName: "Whiff%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.whiffrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swing_p",
    headerName: "Swing%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.swing.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zswing_p",
    headerName: "Z-Swing%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.zswing.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zcontact_p",
    headerName: "Z-Contact%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.zcontact.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swstr_p",
    headerName: "SwStr%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.swstr.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "spd_p",
    headerName: "Speed",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.spd.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "wbsr_pa_p",
    headerName: "wBsR/PA",
    width: 120,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.wbsr_pa.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "pull_p",
    headerName: "Pull",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.pull.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "pullair_p",
    headerName: "Pull Air",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.pullair.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "gbrate_p",
    headerName: "GB%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.gbrate != null ? Number(params.row.gbrate).toFixed(1) : "-"}
        </Box>
      );
    },
  },
  { field: "spd", headerName: "Speed", width: 90 },
  { field: "wbsr", headerName: "wBsR", width: 90 },
  { field: "wbsr_pa", headerName: "wBsR/PA", width: 90 },
  {
    field: "xwoba", headerName: "xwOBA", width: 90, valueFormatter: (value: number) => {
      return value == null ? "" : Number(value).toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "woba", headerName: "wOBA", width: 90, valueFormatter: (value: number) => {
      return value == null ? "" : Number(value).toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "wobacon", headerName: "wOBACON", width: 100, valueFormatter: (value: number) => {
      return value == null ? "" : Number(value).toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "xba", headerName: "xBA", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "xslg", headerName: "xSLG", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  { field: "barrelbbe", headerName: "Barrel%", width: 90 },
  { field: "bbrate", headerName: "BB%", width: 90 },
  { field: "chaserate", headerName: "Chase%", width: 90 },
  { field: "ev", headerName: "EV", width: 90 },
  { field: "maxev", headerName: "Max EV", width: 90 },
  { field: "ev50", headerName: "50th% EV", width: 100 },
  { field: "ev90", headerName: "90th% EV", width: 100 },
  { field: "hhrate", headerName: "Hard Hit%", width: 90 },
  { field: "krate", headerName: "K%", width: 90 },
  { field: "langle", headerName: "L°", width: 90 },
  { field: "whiffrate", headerName: "Whiff%", width: 90 },
  { field: "swing", headerName: "Swing%", width: 90 },
  { field: "zswing", headerName: "Z-Swing%", width: 90 },
  { field: "zcontact", headerName: "Z-Contact%", width: 90 },
  { field: "swstr", headerName: "SwStr%", width: 90 },
  { field: "pull", headerName: "Pull", width: 90 },
  { field: "pullair", headerName: "Pull Air", width: 120 },
  { field: "gbrate", headerName: "GB%", width: 90 },
  {
    field: "ba", headerName: "BA", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "obp", headerName: "OBP", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "slg", headerName: "SLG", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "babip", headerName: "BABIP", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  { field: "barrelpa", headerName: "PA/Barrels", width: 90 },
  { field: "barrels", headerName: "Barrels", width: 90 },
  { field: "bb", headerName: "BB", width: 90 },
  { field: "doubles", headerName: "2B", width: 90 },
  { field: "triples", headerName: "3B", width: 90 },
  { field: "hits", headerName: "Hits", width: 90 },
  { field: "hr", headerName: "HR", width: 90 },
  { field: "k", headerName: "K", width: 90 },
  { field: "swings", headerName: "Swings", width: 90 },
  { field: "whiffs", headerName: "Whiffs", width: 90 },
];

export const hitter_columns: GridColDef<any>[] = labelPercentileColumns(
  withNumericFilters(hitter_columns_defs)
);

const pitcher_columns_defs: GridColDef<any>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Link href={"/player/" + params.row.id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const team = teamColors[params.value] ? params.value : "FA";
      const color = teamColors[team][0]

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[team][2],
            textShadow: "-1px -1px 0 " + teamColors[team][1] + ", 1px -1px 0 " + teamColors[team][1] + ", -1px 1px 0 " + teamColors[team][1] + ", 1px 1px 0 " + teamColors[team][1] + "",
          }}
        >
          {(params.value)}
        </Box>
      );
    },
  },
  { field: "role", headerName: "Pos", width: 60 },
  { field: "age", headerName: "Age", width: 60 },
  {
    field: "ip",
    headerName: "IP",
    width: 60,
    valueFormatter: (value: number) =>
      value === null || value === undefined ? "" : Number(value).toFixed(1),
  },
  { field: "pa", headerName: "PA", width: 60 },
  { field: "ab", headerName: "AB", width: 60 },
  { field: "pitches", headerName: "Pitches", width: 70 },
  {
    field: "xwoba",
    headerName: "xwOBA",
    width: 70,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "woba",
    headerName: "wOBA",
    width: 70,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "score_p",
    headerName: "PS Score",
    width: 120,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "p_agg",
    headerName: "Agg",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "power_agg",
    headerName: "Contact Quality",
    width: 120,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {(params.value * 100).toFixed(2)}%
        </Box>
      );
    },
  },
  {
    field: "velo_p",
    headerName: "Max Velo",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.max_velo.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "release_extension_p",
    headerName: "Extension",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.release_extension != null
            ? Number(params.row.release_extension).toFixed(1)
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "xwoba_p",
    headerName: "xwOBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xwoba.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xba_p",
    headerName: "xBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xba.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xslg_p",
    headerName: "xSLG",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.xslg.toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "barrelbbe_p",
    headerName: "Barrel%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.barrelbbe.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "bbrate_p",
    headerName: "BB%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.bbrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "chaserate_p",
    headerName: "Chase%",
    width: 100,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.chaserate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev_p",
    headerName: "EV",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.ev.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "hhrate_p",
    headerName: "Hard Hit%",
    width: 110,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.hhrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "krate_p",
    headerName: "K%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.krate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "langle_p",
    headerName: "L°",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.langle.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "whiffrate_p",
    headerName: "Whiff%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.whiffrate.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swing_p",
    headerName: "Swing%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.swing.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zswing_p",
    headerName: "Z-Swing%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.zswing.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zcontact_p",
    headerName: "Z-Contact%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.zcontact.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swstr_p",
    headerName: "SwStr%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.swstr.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "woba_p",
    headerName: "wOBA",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.woba != null
            ? Number(params.row.woba).toFixed(3).toString().replace(/^0/, "")
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "strikerate_p",
    headerName: "Strike%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.strikerate != null
            ? Number(params.row.strikerate).toFixed(1)
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "zonerate_p",
    headerName: "Zone%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.zonerate != null
            ? Number(params.row.zonerate).toFixed(1)
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "gbrate_p",
    headerName: "GB%",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.row.gbrate != null ? Number(params.row.gbrate).toFixed(1) : "-"}
        </Box>
      );
    },
  },
  {
    field: "xba", headerName: "xBA", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "xslg", headerName: "xSLG", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  { field: "barrelbbe", headerName: "Barrel%", width: 90 },
  { field: "bbrate", headerName: "BB%", width: 90 },
  { field: "chaserate", headerName: "Chase%", width: 90 },
  { field: "ev", headerName: "EV", width: 90 },
  { field: "hhrate", headerName: "Hard Hit%", width: 90 },
  { field: "krate", headerName: "K%", width: 90 },
  { field: "langle", headerName: "L°", width: 90 },
  { field: "whiffrate", headerName: "Whiff%", width: 90 },
  { field: "strikerate", headerName: "Strike%", width: 90 },
  { field: "zonerate", headerName: "Zone%", width: 90 },
  { field: "gbrate", headerName: "GB%", width: 90 },
  {
    field: "max_velo", headerName: "Max Velo", width: 100, valueFormatter: (value: number) => {
      return value == null ? "" : Number(value).toFixed(1);
    },
  },
  {
    field: "release_extension", headerName: "Extension", width: 100, valueFormatter: (value: number) => {
      return value == null ? "" : Number(value).toFixed(1);
    },
  },
  {
    field: "ba", headerName: "BA", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "obp", headerName: "OBP", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "slg", headerName: "SLG", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  {
    field: "babip", headerName: "BABIP", width: 90, valueFormatter: (value: number) => {
      return value.toFixed(3).toString().replace(/^0/, "");
    },
  },
  { field: "barrelpa", headerName: "PA/Barrels", width: 90 },
  { field: "barrels", headerName: "Barrels", width: 90 },
  { field: "bb", headerName: "BB", width: 90 },
  { field: "doubles", headerName: "2B", width: 90 },
  { field: "triples", headerName: "3B", width: 90 },
  { field: "hits", headerName: "Hits", width: 90 },
  { field: "hr", headerName: "HR", width: 90 },
  { field: "k", headerName: "K", width: 90 },
  { field: "swings", headerName: "Swings", width: 90 },
  { field: "whiffs", headerName: "Whiffs", width: 90 },
  { field: "swing", headerName: "Swing%", width: 90 },
  { field: "zswing", headerName: "Z-Swing%", width: 90 },
  { field: "zcontact", headerName: "Z-Contact%", width: 90 },
  { field: "swstr", headerName: "SwStr%", width: 90 },
];

export const pitcher_columns: GridColDef<any>[] = labelPercentileColumns(
  withNumericFilters(pitcher_columns_defs)
);

const stuff_columns_defs: GridColDef<any>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Link href={"/player/" + params.row.id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const team = teamColors[params.value] ? params.value : "FA";
      const color = teamColors[team][0]

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[team][2],
            textShadow: "-1px -1px 0 " + teamColors[team][1] + ", 1px -1px 0 " + teamColors[team][1] + ", -1px 1px 0 " + teamColors[team][1] + ", 1px 1px 0 " + teamColors[team][1] + "",
          }}
        >
          {(params.value)}
        </Box>
      );
    },
  },
  { field: "role", headerName: "Pos", width: 60 },
  {
    field: "total_pitches",
    headerName: "Pitches",
    width: 100,
  },
  {
    field: "psStuffAgg",
    headerName: "psStuff+",
    width: 120,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffFF",
    headerName: "FF",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffSI",
    headerName: "SI",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffFC",
    headerName: "FC",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffFS",
    headerName: "FS",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffSL",
    headerName: "SL",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffST",
    headerName: "ST",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffCU",
    headerName: "CU",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffKC",
    headerName: "KC",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffCH",
    headerName: "CH",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffSV",
    headerName: "SV",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
  {
    field: "psStuffKN",
    headerName: "KN",
    width: 80,
    valueFormatter: (value: number) => {
      return value === 0 ? "-" : value.toFixed(1);
    },
  },
];

export const stuff_columns: GridColDef<any>[] =
  withNumericFilters(stuff_columns_defs);

const mobile_width = 25;

export const hitter_columns_mobile: GridColDef<any>[] = hitter_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const pitcher_columns_mobile: GridColDef<any>[] = pitcher_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const stuff_columns_mobile: GridColDef<any>[] = stuff_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const hitter_pitch_columns: GridColDef<any>[] = withNumericFilters([
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box sx={{ width: "100%", height: "100%", textAlign: "center" }}>
          <Link href={"/player/" + params.row.player_id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const org = params.value in teamColors ? params.value : "FA";
      const color = teamColors[org][0];
      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[org][2],
            textShadow:
              "-1px -1px 0 " + teamColors[org][1] +
              ", 1px -1px 0 " + teamColors[org][1] +
              ", -1px 1px 0 " + teamColors[org][1] +
              ", 1px 1px 0 " + teamColors[org][1],
          }}
        >
          {params.value}
        </Box>
      );
    },
  },
  { field: "Position", headerName: "Pos", width: 70 },
  {
    field: "pitch_name",
    headerName: "Pitch",
    width: 90,
  },
  { field: "pitches", headerName: "Pitches", width: 75 },
  {
    field: "batter_run_value_per_100_p",
    headerName: "RV/100",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.batter_run_value_per_100).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "batter_run_value_p",
    headerName: "RV",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.batter_run_value).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "woba_p",
    headerName: "wOBA",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.woba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "wobacon_p",
    headerName: "wOBACON",
    width: 100,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {params.row.wobacon != null
            ? Number(params.row.wobacon).toFixed(3).toString().replace(/^0/, "")
            : "-"}
        </Box>
      );
    },
  },
  {
    field: "xwoba_p",
    headerName: "xwOBA",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xwoba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "ba_p",
    headerName: "BA",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.ba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xba_p",
    headerName: "xBA",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "slg_p",
    headerName: "SLG",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.slg).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xslg_p",
    headerName: "xSLG",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xslg).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "swing_miss_percent_p",
    headerName: "Whiff%",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.swing_miss_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "k_percent_p",
    headerName: "K%",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.k_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "hardhit_percent_p",
    headerName: "Hard Hit%",
    width: 100,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.hardhit_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "barrels_per_bbe_percent_p",
    headerName: "Barrel%",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.barrels_per_bbe_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "launch_speed_p",
    headerName: "EV",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.launch_speed).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "velocity",
    headerName: "Velo",
    width: 70,
    valueFormatter: (value: any) => Number(value).toFixed(1),
  },
]);

export const hitter_pitch_columns_mobile: GridColDef<any>[] = hitter_pitch_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const pitcher_pitch_columns: GridColDef<any>[] = withNumericFilters([
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box sx={{ width: "100%", height: "100%", textAlign: "center" }}>
          <Link href={"/player/" + params.row.player_id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const org = params.value in teamColors ? params.value : "FA";
      const color = teamColors[org][0];
      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[org][2],
            textShadow:
              "-1px -1px 0 " + teamColors[org][1] +
              ", 1px -1px 0 " + teamColors[org][1] +
              ", -1px 1px 0 " + teamColors[org][1] +
              ", 1px 1px 0 " + teamColors[org][1],
          }}
        >
          {params.value}
        </Box>
      );
    },
  },
  { field: "role", headerName: "Role", width: 60 },
  {
    field: "pitch_name",
    headerName: "Pitch",
    width: 90,
  },
  { field: "pitches", headerName: "Pitches", width: 75 },
  {
    field: "psStuff_p",
    headerName: "psStuff+",
    width: 100,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.psStuff) === 0 ? "-" : Number(params.row.psStuff).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "pitcher_run_value_per_100_p",
    headerName: "RV/100",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.pitcher_run_value_per_100).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "pitcher_run_value_p",
    headerName: "RV",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.pitcher_run_value).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "velocity",
    headerName: "Velo",
    width: 70,
    valueFormatter: (value: any) => Number(value).toFixed(1),
  },
  {
    field: "xwoba_p",
    headerName: "xwOBA",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xwoba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "woba_p",
    headerName: "wOBA",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.woba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "swing_miss_percent_p",
    headerName: "Whiff%",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.swing_miss_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "k_percent_p",
    headerName: "K%",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.k_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "bb_percent_p",
    headerName: "BB%",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.bb_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "xba_p",
    headerName: "xBA",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xba).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "xslg_p",
    headerName: "xSLG",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.xslg).toFixed(3).toString().replace(/^0/, "")}
        </Box>
      );
    },
  },
  {
    field: "hardhit_percent_p",
    headerName: "Hard Hit%",
    width: 100,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.hardhit_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "barrels_per_bbe_percent_p",
    headerName: "Barrel%",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.barrels_per_bbe_percent).toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "launch_speed_p",
    headerName: "EV",
    width: 80,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.launch_speed).toFixed(1)}
        </Box>
      );
    },
  },
]);

export const pitcher_pitch_columns_mobile: GridColDef<any>[] = pitcher_pitch_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

// Framing scale: 0 = red (bad framer), 1 = blue (good framer) — inverted from default
const framingScale = chroma.scale([
  "rgba(255,0,0,0.5)",
  "#C2C2C2",
  "rgba(0,0,255,0.5)",
]);

export const framing_columns: GridColDef<any>[] = withNumericFilters([
  {
    field: "player_name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box sx={{ width: "100%", height: "100%", textAlign: "center" }}>
          <Link href={"/player/" + params.row.fielder_2} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 ? value : "FA";
    },
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const org = params.value in teamColors ? params.value : "FA";
      const color = teamColors[org][0];
      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[org][2],
            textShadow:
              "-1px -1px 0 " + teamColors[org][1] +
              ", 1px -1px 0 " + teamColors[org][1] +
              ", -1px 1px 0 " + teamColors[org][1] +
              ", 1px 1px 0 " + teamColors[org][1],
          }}
        >
          {params.value}
        </Box>
      );
    },
  },
  { field: "called_pitches", headerName: "Miscalls", width: 80 },
  {
    field: "framing_runs_p",
    headerName: "Framing Runs",
    width: 140,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = framingScale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.framing_runs).toFixed(2)}
        </Box>
      );
    },
  },
  {
    field: "framing_per_1000_p",
    headerName: "FR/1000",
    width: 100,
    cellClassName: () => "super-app",
    renderCell: (params: GridRenderCellParams) => {
      const color = framingScale(params.value).toString();
      return (
        <Box sx={{ backgroundColor: color, width: "100%", height: "100%", textAlign: "center" }}>
          {Number(params.row.framing_per_1000).toFixed(2)}
        </Box>
      );
    },
  },
  { field: "stolen_cs", headerName: "Stolen CS", width: 100 },
  { field: "missed_cs", headerName: "Missed CS", width: 100 },
]);

export const framing_columns_mobile: GridColDef<any>[] = framing_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

// ─── Organization aggregates ────────────────────────────────────────────────
// One row per MLB org. The heat map is driven by the `<stat>_p` field the API
// returns while the cell prints the raw org-level value.

const orgTeamCell = (params: GridRenderCellParams) => {
  const team = teamColors[params.value] ? params.value : "FA";
  return (
    <Box
      sx={{
        backgroundColor: teamColors[team][0],
        width: "100%",
        height: "100%",
        textAlign: "center",
        fontWeight: "bold",
        color: teamColors[team][2],
        textShadow:
          "-1px -1px 0 " + teamColors[team][1] + ", 1px -1px 0 " + teamColors[team][1] +
          ", -1px 1px 0 " + teamColors[team][1] + ", 1px 1px 0 " + teamColors[team][1],
      }}
    >
      {params.value}
    </Box>
  );
};

const orgHeat = (
  stat: string,
  headerName: string,
  digits: number,
  opts: { width?: number; trimZero?: boolean; plus?: boolean; pct?: boolean } = {}
): GridColDef<any> => ({
  field: stat + "_p",
  headerName,
  width: opts.width ?? 90,
  cellClassName: () => "super-app",
  filterOperators: numericOperatorsOn((row) => {
    const raw = row[stat];
    if (raw == null || Number.isNaN(Number(raw))) return null;
    return opts.pct ? Number(raw) * 100 : Number(raw);
  }),
  renderCell: (params: GridRenderCellParams) => {
    const raw = params.row[stat];
    const pct = params.value;
    let text = "-";
    if (raw != null && !Number.isNaN(Number(raw))) {
      text = (opts.pct ? Number(raw) * 100 : Number(raw)).toFixed(digits);
      if (opts.trimZero) text = text.replace(/^(-?)0\./, "$1.");
      if (opts.plus && Number(raw) > 0) text = "+" + text;
      if (opts.pct) text = text + "%";
    }
    return (
      <Box
        sx={{
          backgroundColor: pct == null ? "#E8E8E8" : scale(pct).toString(),
          width: "100%",
          height: "100%",
          textAlign: "center",
        }}
      >
        {text}
      </Box>
    );
  },
});

const orgIdentityColumns: GridColDef<any>[] = [
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 70,
    cellClassName: () => "super-app",
    renderCell: orgTeamCell,
  },
  { field: "players", headerName: "Players", width: 80 },
];

export const org_hitter_columns: GridColDef<any>[] = withNumericFilters([
  ...orgIdentityColumns,
  { field: "pa", headerName: "PA", width: 80 },
  { field: "pitches", headerName: "Pitches", width: 90 },
  orgHeat("score", "PS Score", 1, { width: 100, pct: true }),
  orgHeat("age", "Age", 2, { width: 70 }),
  orgHeat("age_rel", "Age v Lvl", 2, { width: 95, plus: true }),
  orgHeat("wrcplus", "wRC+", 0, { width: 80 }),
  orgHeat("woba", "wOBA", 3, { trimZero: true }),
  orgHeat("xwoba", "xwOBA", 3, { trimZero: true }),
  orgHeat("ba", "AVG", 3, { width: 80, trimZero: true }),
  orgHeat("slg", "SLG", 3, { width: 80, trimZero: true }),
  orgHeat("xslg", "xSLG", 3, { width: 80, trimZero: true }),
  orgHeat("iso", "ISO", 3, { width: 80, trimZero: true }),
  orgHeat("krate", "K%", 1, { width: 75 }),
  orgHeat("bbrate", "BB%", 1, { width: 75 }),
  orgHeat("hrrate", "HR%", 1, { width: 75 }),
  orgHeat("swing", "Swing%", 1 ),
  orgHeat("zswing", "Z-Swing%", 1, { width: 100 }),
  orgHeat("chaserate", "Chase%", 1 ),
  orgHeat("zonerate", "Zone%", 1, { width: 85 }),
  orgHeat("whiffrate", "Whiff%", 1 ),
  orgHeat("swstr", "SwStr%", 1 ),
  orgHeat("zcontact", "Z-Contact%", 1, { width: 110 }),
  orgHeat("ev", "EV", 1, { width: 75 }),
  orgHeat("maxev", "Max EV", 1 ),
  orgHeat("ev90", "EV90", 1, { width: 85 }),
  orgHeat("hhrate", "Hard Hit%", 1, { width: 105 }),
  orgHeat("barrelbbe", "Barrel%", 1 ),
  orgHeat("langle", "LA", 1, { width: 75 }),
  orgHeat("gbrate", "GB%", 1, { width: 80 }),
  orgHeat("ldrate", "LD%", 1, { width: 80 }),
  orgHeat("fbrate", "FB%", 1, { width: 80 }),
  orgHeat("pull", "Pull%", 1, { width: 85 }),
  orgHeat("pullair", "Pulled Air%", 1, { width: 115 }),
  orgHeat("spd", "Spd", 1, { width: 75 }),
]);

export const org_pitcher_columns: GridColDef<any>[] = withNumericFilters([
  ...orgIdentityColumns,
  { field: "bf", headerName: "BF", width: 80 },
  { field: "ip", headerName: "IP", width: 80 },
  orgHeat("score", "PS Score", 1, { width: 100, pct: true }),
  orgHeat("age", "Age", 2, { width: 70 }),
  orgHeat("age_rel", "Age v Lvl", 2, { width: 95, plus: true }),
  orgHeat("era", "ERA", 2, { width: 80 }),
  orgHeat("fip", "FIP", 2, { width: 80 }),
  orgHeat("xfip", "xFIP", 2, { width: 80 }),
  orgHeat("whip", "WHIP", 2, { width: 80 }),
  orgHeat("hr9", "HR/9", 2, { width: 80 }),
  orgHeat("krate", "K%", 1, { width: 75 }),
  orgHeat("bbrate", "BB%", 1, { width: 75 }),
  orgHeat("kbb", "K-BB%", 1, { width: 90 }),
  orgHeat("woba", "wOBA", 3, { trimZero: true }),
  orgHeat("xwoba", "xwOBA", 3, { trimZero: true }),
  orgHeat("xslg", "xSLG", 3, { width: 80, trimZero: true }),
  orgHeat("velo", "Velo", 1, { width: 80 }),
  orgHeat("max_velo", "Max Velo", 1, { width: 100 }),
  orgHeat("spin_rate", "Spin", 0, { width: 80 }),
  orgHeat("release_extension", "Ext", 2, { width: 80 }),
  orgHeat("arm_angle", "Arm Angle", 1, { width: 105 }),
  orgHeat("strikerate", "Strike%", 1 ),
  orgHeat("zonerate", "Zone%", 1, { width: 85 }),
  orgHeat("swing", "Swing%", 1 ),
  orgHeat("zswing", "Z-Swing%", 1, { width: 100 }),
  orgHeat("chaserate", "Chase%", 1 ),
  orgHeat("whiffrate", "Whiff%", 1 ),
  orgHeat("swstr", "SwStr%", 1 ),
  orgHeat("zcontact", "Z-Contact%", 1, { width: 110 }),
  orgHeat("ev", "EV", 1, { width: 75 }),
  orgHeat("hhrate", "Hard Hit%", 1, { width: 105 }),
  orgHeat("barrelbbe", "Barrel%", 1 ),
  orgHeat("langle", "LA", 1, { width: 75 }),
  orgHeat("gbrate", "GB%", 1, { width: 80 }),
  orgHeat("ldrate", "LD%", 1, { width: 80 }),
  orgHeat("fbrate", "FB%", 1, { width: 80 }),
  orgHeat("pull", "Pull%", 1, { width: 85 }),
]);

export const org_hitter_columns_mobile: GridColDef<any>[] = org_hitter_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const org_pitcher_columns_mobile: GridColDef<any>[] = org_pitcher_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

